import { useState, useRef } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { confermaRichiesta, type RichiestaDetail } from '../../api/richieste';

interface Props {
  richiestaId: string;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
}

const inputCls =
  'w-full text-sm border border-stone-border rounded px-3 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

/**
 * Transizione 3 (inviato_cliente → confermato, #75 + #95).
 * Upload del preventivo firmato dal cliente come multipart;
 * il documento risultante è salvato con origine='firmato'.
 */
export default function ConfermaModal({ richiestaId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function handleConferma() {
    if (!file) {
      setError(t('richieste.conferma.val_file'));
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError(t('richieste.conferma.val_pdf'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const updated = await confermaRichiesta(richiestaId, file, note.trim() || null);
      onSaved(updated);
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { detail?: string } } })?.response;
      setError(resp?.data?.detail ?? t('richieste.conferma.errore'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">{t('richieste.conferma.titolo')}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-ink-mid">
            {t('richieste.conferma.descrizione')}
          </p>

          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-2 border-dashed border-stone-border rounded-lg hover:bg-stone-bg transition-colors text-ink-mid"
          >
            <Upload size={16} />
            {file ? t('richieste.conferma.sostituisci_pdf') : t('richieste.conferma.seleziona_pdf')}
          </button>
          {file && (
            <div className="flex items-center gap-2 px-3 py-2 bg-stone-bg/60 border border-stone-mid rounded text-sm text-ink-dark">
              <FileText size={14} className="text-ink-soft" />
              <span className="truncate flex-1">{file.name}</span>
              <span className="text-xs text-ink-soft">
                {(file.size / 1024).toFixed(0)} KB
              </span>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">{t('richieste.conferma.note')}</label>
            <textarea
              className={inputCls}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {error && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
          >
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={handleConferma}
            disabled={loading || !file}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
          >
            {loading ? t('richieste.conferma.conferma_in_corso') : t('richieste.conferma.btn_conferma')}
          </button>
        </div>
      </div>
    </div>
  );
}
